import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useAuth } from '@/contexts/AuthContext';
import { useSpaceData } from '@/hooks/useSpaceData';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ExternalLink, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { getCustomToolKey } from '@/config/tools';

export default function CustomToolPage() {
  const { projectId, toolSlug } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [iframeError, setIframeError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const { space, spaceTools, loading, accessDenied } = useSpaceData(
    projectId,
    userProfile?.id,
    company?.id
  );

  // Find the custom tool from space configuration
  const customTool = useMemo(() => {
    if (!space?.tools?._custom) return null;
    return space.tools._custom.find(
      ct => ct.slug === toolSlug || ct.id === toolSlug
    );
  }, [space?.tools, toolSlug]);

  usePageTitle(customTool?.name ? `${customTool.name} - ${space?.name || 'Space'}` : 'Custom Tool');

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setIframeError(false);
  }, []);

  // Handle iframe error
  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setIframeError(true);
  }, []);

  // Refresh iframe
  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setIframeError(false);
    setRefreshKey(prev => prev + 1);
  }, []);

  // Open in new tab
  const handleOpenNewTab = useCallback(() => {
    if (customTool?.url) {
      window.open(customTool.url, '_blank', 'noopener,noreferrer');
    }
  }, [customTool?.url]);

  // Handle access denied
  if (accessDenied) {
    return (
      <AppShell3>
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <AlertTriangle className="h-12 w-12 text-amber-500" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            You no longer have access to this space.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </AppShell3>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell3>
    );
  }

  // Handle tool not found
  if (!customTool) {
    return (
      <AppShell3>
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <AlertTriangle className="h-12 w-12 text-amber-500" />
          <h2 className="text-xl font-semibold">Tool Not Found</h2>
          <p className="text-muted-foreground text-center max-w-md">
            This custom tool does not exist or has been removed from this space.
          </p>
          <Button onClick={() => navigate(`/space/${projectId}`)}>
            Back to Space
          </Button>
        </div>
      </AppShell3>
    );
  }

  // Build sandbox attribute
  const sandboxAttr = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          spaceImage={space?.avatar}
          toolName={customTool.name}
          currentTool={getCustomToolKey(customTool.id)}
          spaceTools={spaceTools || []}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenNewTab}
              >
                <ExternalLink className="h-4 w-4 mr-1.5" />
                Open in New Tab
              </Button>
            </div>
          }
        />

        <div className="relative flex-1 w-full min-h-0">
          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading {customTool.name}...</p>
              </div>
            </div>
          )}

          {/* Error state */}
          {iframeError ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
              <Alert variant="destructive" className="max-w-md">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load the external tool. This may be because:
                  <ul className="list-disc ml-4 mt-2 space-y-1">
                    <li>The URL is incorrect or the service is unavailable</li>
                    <li>The website blocks embedding in iframes</li>
                    <li>Your network is blocking the connection</li>
                  </ul>
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleOpenNewTab}>
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Open in New Tab
                </Button>
              </div>
            </div>
          ) : (
            /* Iframe */
            <iframe
              key={`${customTool.id}-${refreshKey}`}
              src={customTool.url}
              title={customTool.name}
              className="absolute inset-0 w-full h-full border-0"
              sandbox={sandboxAttr}
              allow="clipboard-write; clipboard-read"
              referrerPolicy="strict-origin-when-cross-origin"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
      </PageTransition>
    </AppShell3>
  );
}
